// @ts-nocheck
import * as React from "react";
import { Sidebar, Topbar, AppFooter, Ic, useCurrentUser, apiErrorMessage } from "./common";
import { usePermissionsFromSession } from "../components/permissions";

// 통합돌봄 청구(Claim) 목록·제출 화면. 어르신 × 서비스코드 × 월 조합 청구 단위를
// 표로 표시하고, 초안(draft) 은 "제출"·submitted 는 "반려" 액션을 붙인다.
// XLSX(현행: CSV+BOM) 다운로드로 장기요양보험/지자체 시스템에 업로드 준비.
const STATUS_LABEL = { draft: "초안", submitted: "제출됨", paid: "입금", rejected: "반려" };
const STATUS_COLOR = { draft: "gray", submitted: "sky", paid: "green", rejected: "rose" };

function ymNow() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; }

function Claims() {
  const { user, loaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded });
  const [claims, setClaims] = React.useState(null);
  const [summary, setSummary] = React.useState({});
  const [err, setErr] = React.useState(null);
  const [ym, setYm] = React.useState(() => new URLSearchParams(window.location.search).get("month") || ymNow());
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    setClaims(null); setErr(null);
    const qs = new URLSearchParams();
    if (ym) qs.set("year_month", ym);
    if (statusFilter !== "all") qs.set("status", statusFilter);
    fetch(`/api/v1/claims?${qs.toString()}`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(j)))
      .then(j => { setClaims(j.claims || []); setSummary(j.summary || {}); })
      .catch(e => setErr(apiErrorMessage(e)));
  }, [ym, statusFilter]);
  React.useEffect(load, [load]);

  const act = async (id, action, extra = {}) => {
    setBusy(true);
    try {
      const r = await fetch(`/api/v1/claims/${id}/${action}`, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(extra),
      });
      if (!r.ok) { const j = await r.json().catch(()=>({})); throw new Error(apiErrorMessage(j, r.status)); }
      load();
    } catch (e) { alert(e.message || String(e)); } finally { setBusy(false); }
  };

  if (loaded && !perms.canManageClaims) {
    return <div className="app" style={{padding:40, color:'var(--rose-500)'}}>청구 관리 권한이 없습니다.</div>;
  }

  const totals = summary?.count_by_status || {};
  const total  = Object.values(totals).reduce((a,b)=>a+b, 0);

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar active="claims"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="청구 관리" sub={`${ym} · ${total}건`}/>
          <div className="main">

            <div className="card" style={{padding:'14px 22px', display:'flex', alignItems:'center', gap:12, marginBottom:14}}>
              <div className="label" style={{margin:0}}>청구년월</div>
              <input type="month" className="input" value={ym} onChange={e=>setYm(e.target.value)} style={{width:150}}/>
              <select className="select" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{width:130}}>
                <option value="all">전체 상태</option>
                {Object.entries(STATUS_LABEL).map(([k,l])=><option key={k} value={k}>{l}</option>)}
              </select>
              <div style={{flex:1}}/>
              <a className="btn btn-ghost btn-md"
                href={`/api/v1/claims/export.csv?year_month=${ym}`} style={{textDecoration:'none'}}>
                {Ic.arrow} CSV
              </a>
            </div>

            {/* 요약 카운트 */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:14}}>
              {["draft","submitted","paid","rejected"].map(k => (
                <div key={k} className="card kpi">
                  <div className="lbl">{STATUS_LABEL[k]}</div>
                  <div className="val" style={{color:`var(--${STATUS_COLOR[k]}-600)`}}>{totals[k] || 0}건</div>
                </div>
              ))}
            </div>

            <div className="card" style={{overflow:'hidden'}}>
              {err && <div style={{padding:14, color:'var(--rose-500)'}}>{err}</div>}
              {claims === null && !err && <div className="muted" style={{padding:14}}>불러오는 중…</div>}
              {claims && claims.length === 0 && <div className="muted" style={{padding:14}}>해당 조건의 청구가 없습니다.</div>}
              {claims && claims.length > 0 && (
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
                  <thead>
                    <tr style={{background:'var(--bg-sub)'}}>
                      {["월","서비스","어르신","담당매니저","방문","분","상태","작업"].map(h => (
                        <th key={h} style={{padding:'10px 12px', textAlign:'left', fontWeight:700, color:'var(--ink-3)'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map(c => (
                      <tr key={c.id} style={{borderTop:'1px solid var(--border-soft)'}}>
                        <td style={{padding:'8px 12px'}} className="mono">{c.year_month}</td>
                        <td style={{padding:'8px 12px'}}>
                          <span className="mono" style={{fontWeight:700}}>{c.service_code}</span>
                          <span className="muted" style={{marginLeft:6}}>{c.service_label}</span>
                        </td>
                        <td style={{padding:'8px 12px', fontWeight:600}}>{c.elderly_name}</td>
                        <td style={{padding:'8px 12px'}}>{c.care_manager_name || <span className="muted">-</span>}</td>
                        <td style={{padding:'8px 12px', textAlign:'right'}} className="mono">{c.visit_count}회</td>
                        <td style={{padding:'8px 12px', textAlign:'right'}} className="mono">{c.total_minutes}분</td>
                        <td style={{padding:'8px 12px'}}>
                          <span className={"badge badge-" + (STATUS_COLOR[c.status] || 'gray')}>{STATUS_LABEL[c.status] || c.status}</span>
                        </td>
                        <td style={{padding:'8px 12px'}}>
                          {c.status === 'draft' && (
                            <button className="btn btn-primary btn-sm" disabled={busy}
                              onClick={()=>{
                                const ref = prompt("제출번호(선택)? — 장기요양보험 접수번호 등을 남기면 감사 추적이 쉬워집니다.", "");
                                act(c.id, 'submit', ref ? { submission_ref: ref } : {});
                              }}>제출</button>
                          )}
                          {c.status === 'submitted' && (
                            <button className="btn btn-ghost btn-sm" disabled={busy}
                              onClick={()=>{
                                const note = prompt("반려 사유", "");
                                if (note) act(c.id, 'reject', { note });
                              }}
                              style={{color:'var(--rose-500)'}}>반려</button>
                          )}
                          {(c.status === 'paid' || c.status === 'rejected') && (
                            <span className="muted" style={{fontSize:11}}>완료 상태</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}

export { Claims };
